import { escapeSpecificMarkdownCharacters } from "@edgetier/utilities";
import styles from "./chat.module.scss";
import { IWritingIssue } from "@edgetier/watchtower-types";
import classNames from "classnames";

/**
 * Annotates a message string with custom markdown syntax to highlight writing issues (grammar/spelling).
 * @param writingIssues The array of writing issues with their positions and descriptions.
 * @param message The original message string to annotate.
 * @returns The message string with writing issue markers inserted.
 */
export const formatWritingIssueMessage = (writingIssues: IWritingIssue[], message: string) => {
    // Split the message by character so we can use the writing issue start/end index to alter the string
    // without messing up the indexes of subsequent writing issues.
    const splitMessage = message.split("");
    // The Markdown component accepts a custom syntax: "**[someClassName]some text**" that allows class name(s)
    // to be added to that section of writing. This is used to highlight spelling and grammar issues.
    writingIssues.forEach(({ description, startIndex, endIndex, writingIssueType }) => {
        // If starting index from backend is a space, we should move one over.
        const startIsEmpty = splitMessage[startIndex] === " ";

        const newStartIndex = startIsEmpty ? startIndex + 1 : startIndex;
        splitMessage[newStartIndex] =
            `**issueStart[${styles[`writing-issue-${writingIssueType.toLowerCase()}`]} writing-issue writing-issue--${writingIssueType.toLowerCase()},${description}]${splitMessage[newStartIndex]}`;

        // Avoid using an index higher than message character array
        const newEndIndex = startIsEmpty ? endIndex + 1 : endIndex;
        const writingIssueEndIndex = Math.min(newEndIndex, splitMessage.length - 1);

        splitMessage[writingIssueEndIndex] = `${splitMessage[writingIssueEndIndex]}issueEnd**`;
    });
    return escapeSpecificMarkdownCharacters(splitMessage.join(""), ["`"]);
};

/**
 * Applies or removes custom markdown class name annotations on specific sentences within a message.
 * @param sentenceToHighlight Array of sentence strings to highlight.
 * @param message The original message string to process.
 * @param options Optional configuration for clearing previous highlights and specifying class names.
 * @returns The message string with class name markers applied.
 */
export const formatClassNamesMessage = (
    sentenceToHighlight: string[],
    message: string,
    options?: { clearPrevious?: boolean; className?: string; additionalClassName?: string | string[] }
) => {
    // The Markdown component accepts a custom syntax: "**[someClassName]some text**" that allows class name(s)
    // to be added to that section of writing. This is used to highlight spelling and grammar issues.
    let messageBuffer = message;
    // Remove all previous highlights
    if (options?.clearPrevious) {
        const previousHighlightsMatches = message.matchAll(/\*\*classNameStart\[(.*),(.*)\]([\s\S]*)classNameEnd\*\*/g);
        previousHighlightsMatches.forEach((match) => {
            messageBuffer = messageBuffer.replace(match[0], match[3]);
        });
    }
    sentenceToHighlight.forEach((sentence) => {
        // Replace each line of the sentence separately so we never wrap content that spans
        // multiple list items in one marker (which would merge them after normalizeHighlightMarkers).
        const lines = sentence
            .split(/\n/)
            .map((line) => line.trim())
            .filter(Boolean);
        lines.forEach((line) => {
            // Strip list marker so we replace only the item text and preserve "- " / "* " / "· " / "1. " etc.
            const itemText = line
                .replace(/^\s*[-*]\s+/, "")
                .replace(/^\s*\d+\.\s+/, "")
                .trim();
            if (itemText === "") return;
            messageBuffer = messageBuffer.replaceAll(
                itemText,
                `**classNameStart[${classNames(options?.className || "highlight", options?.additionalClassName)},]${itemText}classNameEnd**`
            );
        });
    });
    return escapeSpecificMarkdownCharacters(messageBuffer, ["`"]);
};
